// De schrijfstap achter de schakelaar 'Kwalificeringsvragen' op een onderzoek,
// en de herberekening die de meetmoment-schrijvers na elke wijziging aanroepen.
//
// Eén batch per handeling: de vragen en de uitsluitingen op de meetmomenten
// landen samen of helemaal niet. Een half aangezette set (vragen wél, maar ook
// op het tweede meetmoment) is erger dan een foutmelding. Een mislukte batch
// gooit door naar het scherm.
//
// Een vragenlijst kan in meer onderzoeken zitten. Daarom worden de meetmomenten
// van ÁLLE onderzoeken met deze vragenlijst bijgewerkt, en geeft de functie die
// onderzoeken terug zodat het scherm het kan melden.
//
// Uitzetten registreert niets in de prullenbak: alleen de schakelaar haalt de
// vragen terug, met dezelfde ID's.
//
// Directe reads buiten de hooks, dus met `CompagnyID` en `Deleted` met de hand.
// AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch
// AI-CONTEXT: See docs/features/sector-kwalificeringsvragen.md

import firebase from '../../firebase/compat'
import uuid from 'react-uuid'
import { db } from '../../firebase/config'
import notDeleted from '../notDeleted'
import buildQualifyingDocs from './buildQualifyingDocs'
import qualifyingExclusions from './qualifyingExclusions'
import isQualifyingQuestion, { isInQualifyingSet } from './isQualifyingQuestion'

/**
 * @typedef {import('./sectorQualifyingQuestions').QualifyingSet} QualifyingSet
 */

/**
 * @typedef {object} ResearchRef
 * @property {string} ID
 * @property {string} Title
 */

/**
 * @param {string} collection
 * @param {Array<[string, *]>} filters - Gelijkheidsfilters naast `CompagnyID`
 * @param {string} client
 * @returns {Promise<Array<object>>} Documenten met `docid`, ook verwijderde
 */
const fetchDocs = async (collection, filters, client) => {
    let query = db.collection(collection).where('CompagnyID', '==', client)
    filters.forEach(([field, value]) => { query = query.where(field, '==', value) })
    const snapshot = await query.get()
    return snapshot.docs.map((doc) => ({ ...doc.data(), docid: doc.id }))
}

/**
 * De levende onderzoeken met deze vragenlijst, en per onderzoek zijn meetmomenten.
 *
 * @param {string} questionnaireId
 * @param {string} client
 * @returns {Promise<{ researches: Array<object>, momentsPerResearch: Array<Array<object>> }>}
 */
const fetchResearchMoments = async (questionnaireId, client) => {
    const researches = (await fetchDocs('Research', [['QuestionnaireID', questionnaireId]], client)).filter(notDeleted)
    const momentsPerResearch = await Promise.all(
        researches.map((research) => fetchDocs('MeasureMoments', [['ResearchID', research.ID]], client)),
    )
    return { researches, momentsPerResearch }
}

/**
 * @param {Array<Array<object>>} momentsPerResearch
 * @param {Array<string>} questionFieldIds
 * @param {boolean} active
 * @returns {Array<import('./qualifyingExclusions').ExclusionUpdate>}
 */
const exclusionUpdates = (momentsPerResearch, questionFieldIds, active) => (
    momentsPerResearch.flatMap((moments) => qualifyingExclusions(moments, questionFieldIds, { active }))
)

/**
 * @param {object} batch - Firestore WriteBatch
 * @param {Array<import('./qualifyingExclusions').ExclusionUpdate>} updates
 */
const addExclusionUpdates = (batch, updates) => {
    updates.forEach(({ docid, ExcludedFieldIDs }) => {
        batch.update(db.collection('MeasureMoments').doc(docid), { ExcludedFieldIDs })
    })
}

// Firestore weigert een batch van meer dan 500 schrijfacties, met een
// technische fout. Dat gebeurt pas bij honderden meetmomenten die dezelfde
// vragenlijst delen; zeg het dan in gewone taal en schrijf niets.
const MAX_BATCH_WRITES = 500

/**
 * @param {number} writes
 */
const assertBatchFits = (writes) => {
    if (writes > MAX_BATCH_WRITES) {
        throw new Error(`Te veel wijzigingen in één keer (${writes}, maximaal ${MAX_BATCH_WRITES}): deze vragenlijst hoort bij te veel meetmomenten. Neem contact op met Deccos.`)
    }
}

/**
 * Zet een set kwalificeringsvragen aan of uit op een vragenlijst.
 *
 * @param {object} params
 * @param {QualifyingSet} params.set
 * @param {boolean} params.active - true = aanzetten
 * @param {string} params.questionnaireId
 * @param {string} params.client - CompagnyID
 * @param {string} params.userId - Wie het uitzet (`DeletedBy`)
 * @returns {Promise<{ researches: Array<ResearchRef> }>} De onderzoeken die deze vragenlijst gebruiken
 */
const applyQualifyingToggle = async ({ set, active, questionnaireId, client, userId }) => {
    const fields = await fetchDocs('QuestionnaireFields', [['QuestionnaireID', questionnaireId]], client)
    const questionFields = fields.filter((f) => isInQualifyingSet(f, set) && f.Type !== 'section')
    const options = (await Promise.all(
        questionFields.map((f) => fetchDocs('MultipleQuestionOptions', [['Field', f.ID]], client)),
    )).flat()
    const { researches, momentsPerResearch } = await fetchResearchMoments(questionnaireId, client)

    const plan = buildQualifyingDocs({
        set,
        active,
        fields,
        options,
        questionnaireId,
        client,
        timestamp: firebase.firestore.Timestamp.now(),
        makeId: uuid,
    })

    const updates = exclusionUpdates(momentsPerResearch, plan.questionFieldIds, active)
    assertBatchFits(plan.creates.length + plan.restores.length + plan.softDeletes.length + updates.length)

    const { FieldValue } = firebase.firestore
    const batch = db.batch()
    plan.creates.forEach(({ collection, data }) => {
        batch.set(db.collection(collection).doc(), data)
    })
    plan.restores.forEach(({ collection, docid, data }) => {
        batch.update(db.collection(collection).doc(docid), {
            ...data,
            Deleted: FieldValue.delete(),
            DeletedAt: FieldValue.delete(),
            DeletedBy: FieldValue.delete(),
        })
    })
    plan.softDeletes.forEach(({ collection, docid }) => {
        batch.update(db.collection(collection).doc(docid), {
            Deleted: true,
            DeletedAt: FieldValue.serverTimestamp(),
            DeletedBy: userId || '',
        })
    })
    addExclusionUpdates(batch, updates)
    await batch.commit()

    return { researches: researches.map(({ ID, Title }) => ({ ID, Title })) }
}

/**
 * Rekent de uitsluitingen op de meetmomenten opnieuw uit voor de
 * kwalificeringsvragen die op deze vragenlijst aanstaan. Aanroepen na elke
 * wijziging aan meetmomenten (toevoegen, verslepen, verwijderen, terugzetten
 * uit de prullenbak) en na het kiezen van een andere vragenlijst. Schrijft
 * niets als er geen kwalificeringsvragen zijn of niets verandert.
 *
 * Heeft het scherm de velden van de vragenlijst al (een listener), geef ze dan
 * mee: dan leest hij ze niet opnieuw, en zonder kwalificeringsvragen leest hij
 * helemaal niets. Dit loopt na elke meetmomentwijziging bij élke organisatie
 * (performancecheck /complete 2026-10-28).
 *
 * @param {object} params
 * @param {string} params.questionnaireId
 * @param {string} params.client - CompagnyID
 * @param {Array<object>} [params.fields] - De velden van deze vragenlijst, als de aanroeper ze al heeft
 * @returns {Promise<void>}
 */
export const syncQualifyingExclusions = async ({ questionnaireId, client, fields: knownFields }) => {
    if (!questionnaireId) return

    const fields = Array.isArray(knownFields)
        ? knownFields
        : await fetchDocs('QuestionnaireFields', [['QuestionnaireID', questionnaireId]], client)
    const questionFieldIds = fields
        .filter((f) => notDeleted(f) && isQualifyingQuestion(f) && f.Type !== 'section')
        .map((f) => f.ID)
    if (questionFieldIds.length === 0) return

    const { momentsPerResearch } = await fetchResearchMoments(questionnaireId, client)
    const updates = exclusionUpdates(momentsPerResearch, questionFieldIds, true)
    if (updates.length === 0) return
    assertBatchFits(updates.length)

    const batch = db.batch()
    addExclusionUpdates(batch, updates)
    await batch.commit()
}

export default applyQualifyingToggle
